import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';
import { normalizeWeatherServiceUrl } from 'components/MapPage/CorporateViewTabs/EventsTab/weatherFeatureServerCatalog';
import {
  buildPublicSeedLayers,
  type FederalLayerFolder,
  type PublicSeedLayer,
} from './federalPublicLayerCatalog';
import { externalLayersKeys } from 'hooks/externalLayer/queryKeys';
import type { CreateManyExternalLayersBody } from 'hooks/externalLayer/types';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';
import type {
  CreateLayerFolderNodeBody,
  LayerNodeDto,
  ListLayerNodesResponse,
} from 'hooks/layerNode/types';

const ensureLocks = new Map<string, Promise<unknown>>();

type ExistingHubLayer = {
  id: string;
  folderId: string | null;
};

/**
 * Hub layers keyed by normalised service URL and portal item id, plus the
 * folders already in the group's tree. Incident-scoped rows are skipped: a
 * layer created inside an incident should not stop the hub copy from being
 * seeded.
 */
async function readGroupLayerTree(groupGuid: string): Promise<{
  hubLayersByUrl: Map<string, ExistingHubLayer>;
  hubLayersByItemId: Map<string, ExistingHubLayer>;
  folderIdByName: Map<string, string>;
}> {
  const resp = await axios.get<ListLayerNodesResponse>(
    config.apiGateway.layer_nodes,
    {
      params: { 'filter[groupGuid]': groupGuid },
    }
  );

  const hubLayersByUrl = new Map<string, ExistingHubLayer>();
  const hubLayersByItemId = new Map<string, ExistingHubLayer>();
  const folderIdByName = new Map<string, string>();

  for (const group of resp.data.data ?? []) {
    if (group.folder) {
      folderIdByName.set(group.folder.name, group.folder.id);
    }

    for (const item of group.items) {
      const layer = item.layer;
      if (layer.incidentId) continue;

      const existing: ExistingHubLayer = {
        id: layer.id,
        folderId: item.node?.parentId ?? null,
      };

      const serviceUrl = 'serviceUrl' in layer ? layer.serviceUrl : null;
      if (serviceUrl) {
        hubLayersByUrl.set(normalizeWeatherServiceUrl(serviceUrl), existing);
      }

      const itemId = 'arcgisItemId' in layer ? layer.arcgisItemId : null;
      if (itemId) {
        hubLayersByItemId.set(itemId, existing);
      }
    }
  }

  return { hubLayersByUrl, hubLayersByItemId, folderIdByName };
}

function findExistingHubLayer(
  entry: PublicSeedLayer,
  hubLayersByUrl: Map<string, ExistingHubLayer>,
  hubLayersByItemId: Map<string, ExistingHubLayer>
): ExistingHubLayer | undefined {
  if (entry.arcgisItemId) {
    const byItem = hubLayersByItemId.get(entry.arcgisItemId);
    if (byItem) return byItem;
  }
  if (entry.serviceUrl) {
    return hubLayersByUrl.get(normalizeWeatherServiceUrl(entry.serviceUrl));
  }
  return undefined;
}

async function ensureFolder({
  groupGuid,
  name,
  folderIdByName,
}: {
  groupGuid: string;
  name: FederalLayerFolder;
  folderIdByName: Map<string, string>;
}): Promise<string> {
  const existing = folderIdByName.get(name);
  if (existing) return existing;

  const resp = await axios.post<LayerNodeDto>(
    `${config.apiGateway.layer_nodes}/folder`,
    {
      groupGuid,
      name,
      incidentId: null,
      parentId: null,
    } satisfies CreateLayerFolderNodeBody
  );

  folderIdByName.set(name, resp.data.id);
  return resp.data.id;
}

async function assignLayerToFolder({
  groupGuid,
  layerId,
  targetFolderId,
}: {
  groupGuid: string;
  layerId: string;
  targetFolderId: string;
}): Promise<void> {
  await axios.put(
    `${config.apiGateway.layer_nodes}/layer/external/${layerId}/assignment`,
    { groupGuid, targetFolderId }
  );
}

async function ensurePublicExternalLayersForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const { hubLayersByUrl, hubLayersByItemId, folderIdByName } =
    await readGroupLayerTree(groupGuid);

  const seedLayers = buildPublicSeedLayers();

  const missing: PublicSeedLayer[] = [];
  const refile: { layerId: string; folder: FederalLayerFolder }[] = [];

  for (const entry of seedLayers) {
    const existing = findExistingHubLayer(
      entry,
      hubLayersByUrl,
      hubLayersByItemId
    );
    if (!existing) {
      missing.push(entry);
      continue;
    }
    const desiredFolderId = folderIdByName.get(entry.folder);
    if (existing.folderId !== desiredFolderId) {
      refile.push({ layerId: existing.id, folder: entry.folder });
    }
  }

  if (missing.length === 0 && refile.length === 0) {
    return;
  }

  const byFolder = new Map<FederalLayerFolder, PublicSeedLayer[]>();
  for (const entry of missing) {
    const bucket = byFolder.get(entry.folder) ?? [];
    bucket.push(entry);
    byFolder.set(entry.folder, bucket);
  }

  for (const [folder, entries] of byFolder) {
    const targetFolderId = await ensureFolder({
      groupGuid,
      name: folder,
      folderIdByName,
    });

    await axios.post(`${config.apiGateway.external_layers}/create_many`, {
      groupGuid,
      targetFolderId,
      layers: entries.map((entry) => ({
        incidentId: null,
        arcgisItemId: entry.arcgisItemId ?? null,
        serviceUrl: entry.serviceUrl ?? null,
        itemType: entry.itemType,
        name: entry.name,
        description: entry.description,
        defaultVisible: entry.defaultVisible,
        userGuid: null,
        authType: 'public' as const,
      })),
    } satisfies CreateManyExternalLayersBody);
  }

  for (const { layerId, folder } of refile) {
    const targetFolderId = await ensureFolder({
      groupGuid,
      name: folder,
      folderIdByName,
    });
    await assignLayerToFolder({ groupGuid, layerId, targetFolderId });
  }

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: externalLayersKeys.all() }),
    queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() }),
  ]);
}

/**
 * Seed the public federal layer catalog as hub layers for the active
 * organisation. Runs for every org rather than only demo orgs, so the Layers
 * tab carries the same federal baseline everywhere, in the Hub and inside any
 * incident workspace (hub layers are in scope for both).
 */
export function useEnsurePublicExternalLayers() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!groupGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `public-external-layers:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensurePublicExternalLayersForGroup({
        groupGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure public external layers', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [groupGuid, queryClient]);
}
